import * as React from 'react';

import { Autocomplete, AutocompleteProps, createFilterOptions } from '@emburse/ui-core';

import { IUserExtension } from '@ClientModels/userExtension';

import isEmail from 'validator/lib/isEmail';

import { UserAutoCompleteDisplay } from './UserAutoCompleteDisplay';

const PHYSICAL_CARD_HELPER_TEXT = 'Limit one physical card/user/card program. Invite new users using their email id.';
const VIRTUAL_CARD_HELPER_TEXT = 'Issue virtual cards for up to 10 people at a time.';
const ERROR_HELPER_TEXT = 'Select a name from the dropdown or enter a valid email address';

interface IAddableUserAutocompleteProps extends Omit<AutocompleteProps<string>, 'value' | 'options' | 'onChange' | 'children'> {
  selectedId?: string;
  selectedIds?: string[];
  selectedEmail?: string;
  userOptions: IUserExtension[];
  onChange: (newValue?: Partial<IUserExtension>) => void;
}

const filter = createFilterOptions();

/**
 * An Autocomplete for users that also supports adding new users by typing their email
 * Adapted from https://github.com/emburse/embark/blob/next/packages/embark-core/src/Autocomplete/AutocompleteDemo.tsx
 *
 * @param props.selectedId The ID of the selected user.  Should be falsy if there's no selection, or the selected user is a new one that doesn't exist in @param props.userOptions
 * @param props.selectedEmail The email of the selected user.  If the selected user is a new one that doesn't exist in @param props.userOptions, then @param props.selectedId will be falsy and this will be used to identify the user
 * @param props.userOptions The list of existing users
 * @param props.onChange The change handler.  Its argument is Partial<IUserExtension> because if a new email is entered, it will only have the displayName and email properties
 * @returns
 */
export const AddableUserAutocomplete: React.FC<IAddableUserAutocompleteProps> = ({
  selectedId,
  selectedEmail,
  selectedIds,
  userOptions,
  disabled,
  multiple,
  onChange,
  ...rest
}) => {
  const defaultHelperText = multiple ? VIRTUAL_CARD_HELPER_TEXT : PHYSICAL_CARD_HELPER_TEXT;
  const [error, setError] = React.useState(false);
  const [helperText, setHelperText] = React.useState<string | null>(defaultHelperText);
  const [options, setOptions] = React.useState<Partial<IUserExtension>[]>(userOptions);

  const selectedValue = React.useMemo(() => {
    if (multiple) {
      if (selectedIds && selectedIds?.length > 0) {
        return options.filter((option) => selectedIds.find((x) => x === option.id || x === option.email));
      }

      return [];
    }

    if (selectedId) {
      return options.find((option) => option.id === selectedId);
    } else if (selectedEmail) {
      return options.find((option) => option.email === selectedEmail);
    }
    return null;
  }, [selectedId, selectedEmail, selectedIds, multiple, options]);

  const clearError = () => {
    setError(false);
    setHelperText(defaultHelperText);
  };

  const validate = (value: string) => {
    const isValidEmail = isEmail(value);

    if (isValidEmail) {
      clearError();
    } else {
      setHelperText(ERROR_HELPER_TEXT);
      setError(true);
    }

    const existingOption = options.find((option) => option.email?.toLowerCase() === value?.toLowerCase());

    const formattedValue = existingOption || {
      displayName: value,
      email: value
    };

    onChange(formattedValue);

    if (isValidEmail && !existingOption) {
      setOptions([{ email: value, displayName: value }, ...options]);
    }

    return formattedValue;
  };

  const handleOnChangeCreate = (_event: React.ChangeEvent<{}>, newValue: any) => {
    clearError();
    if (disabled) {
      return;
    }

    if (multiple && Array.isArray(newValue)) {
      const filteredValue = [] as any;

      newValue.forEach((val) => {
        if (val && val.inputValue) {
          filteredValue.push(validate(val.inputValue));
        } else {
          filteredValue.push(val);
        }
      });

      onChange(filteredValue);
      return;
    }

    if (typeof newValue === 'string') {
      onChange({
        displayName: newValue,
        email: newValue
      });
    } else if (newValue && newValue.inputValue) {
      validate(newValue.inputValue);
    } else {
      onChange(newValue);
    }
  };

  return (
    <Autocomplete
      {...rest}
      fullWidth
      disabled={disabled}
      onChange={handleOnChangeCreate}
      error={error}
      helperText={helperText}
      options={options}
      autoHighlight
      getOptionLabel={(option: IUserExtension) => {
        return option.displayName;
      }}
      multiple={multiple ? true : false}
      multipleType="chips"
      value={selectedValue}
      renderOption={(props: any, option: IUserExtension) => <UserAutoCompleteDisplay props={props} option={option} />}
      filterOptions={(options: IUserExtension[], params: any) => {
        const filtered = filter(options, params);
        clearError();
        if (params.inputValue !== '') {
          filtered.push({
            inputValue: params.inputValue,
            displayName: `Add "${params.inputValue}"`
          });
        }
        return filtered;
      }}
    />
  );
};
